import { LoaderCircle, Paperclip, Upload, X } from "lucide-react"
import { ReactElement, useRef, useState } from "react"
import { Button } from "../atoms/Button"
import { cn } from "../utils"

const UploadS3InputFormatEnum = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  GIF: "GIF",
  DOCUMENT: "DOCUMENT",
  AUDIO: "AUDIO",
} as const

type UploadS3InputProps = {
  accept?: string
  onChange: ({
    format,
    url,
  }: {
    url: string
    format: keyof typeof UploadS3InputFormatEnum | null
  }) => void
  isError?: boolean
  className?: string
  placeholder: string
  mutateAsync: (file: FileList) => Promise<{ url: string }>
  isPending: boolean
  disabled?: boolean
  component?: (isPending: boolean) => ReactElement
}

function getFileFormat(type: string) {
  // Specific ones
  switch (type) {
    case "image/gif":
      return UploadS3InputFormatEnum.GIF
    case "application/pdf":
      return UploadS3InputFormatEnum.DOCUMENT
  }

  switch (type.split("/")?.[0] ?? "") {
    case "image":
      return UploadS3InputFormatEnum.IMAGE
    case "video":
      return UploadS3InputFormatEnum.VIDEO
    case "audio":
      return UploadS3InputFormatEnum.AUDIO
  }

  return UploadS3InputFormatEnum.DOCUMENT
}

export const UploadS3Input = ({
  onChange,
  accept = "image/*",
  isError,
  className,
  placeholder,
  mutateAsync,
  isPending,
  disabled,
  component,
}: UploadS3InputProps) => {
  const inputRef = useRef<HTMLInputElement>(undefined)
  const [isDragging, setIsDragging] = useState(false)
  const [fileName, setFileName] = useState<string | undefined>(undefined)

  const handleDragIn = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragOut = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    onSelectFiles()
  }

  const handleRemoveFile = () => {
    if (!inputRef.current) return

    inputRef.current.files = null
    inputRef.current.value = ""

    setFileName("")
    onChange({ url: "", format: null })
  }

  const onSelectFiles = async () => {
    if (inputRef.current) {
      const files = inputRef.current.files

      if (files?.item(0)) {
        setFileName(files.item(0)?.name)
        const { url } = await mutateAsync(files)

        onChange({ url, format: getFileFormat(files.item(0)?.type ?? "") })

        if (component) {
          handleRemoveFile()
        }
      }
    }
  }

  const hasFile = !!fileName

  if (component) {
    return (
      <div
        className={cn("relative w-fit", className)}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        {component(isPending)}
        <input
          type="file"
          className={"absolute inset-0 opacity-0 w-0 h-0 cursor-pointer"}
          ref={(e) => {
            inputRef.current = e ?? undefined
          }}
          onChange={onSelectFiles}
          tabIndex={-1}
          accept={accept}
          disabled={isPending || disabled}
        />
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div
        className={cn(
          "relative py-10 px-6 border-dashed rounded-lg border border-border hover:bg-muted focus text-center flex flex-col items-center gap-4 cursor-pointer focus-visible:outline-hidden focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-2 group bg-background",
          hasFile && "hover:bg-muted",
          isDragging && "border-solid border-card bg-muted",
          isError && "border-error",
          disabled && "cursor-not-allowed",
          className,
        )}
        tabIndex={0}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        {!hasFile && (
          <Upload className={cn("text-placeholder w-6 h-6", hasFile && "group-hover:hidden")} />
        )}
        {hasFile && isPending && <LoaderCircle className="text-placeholder w-6 h-6 animate-spin" />}
        {hasFile && !isPending && <Paperclip className="text-placeholder w-6 h-6" />}
        <span className="break-words text-sm px-6 text-placeholder">{fileName ?? placeholder}</span>
        <input
          type="file"
          className={"absolute inset-0 opacity-0 w-0 h-0 cursor-pointer"}
          ref={(e) => {
            inputRef.current = e ?? undefined
          }}
          onChange={onSelectFiles}
          tabIndex={-1}
          accept={accept}
          disabled={isPending || disabled}
        />
        {hasFile && (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              handleRemoveFile()
            }}
            variant="ghost"
            className="absolute top-2 right-2"
            size="icon"
          >
            <X className="text-foreground w-5 h-5" />
          </Button>
        )}
      </div>
    </div>
  )
}
